import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import {
  type ApplyDraft,
  applyProfileChanges,
  createCompanyObjective,
  loadProfileEntries,
  prepareApply,
} from '@/lib/company-apply';
import { CONTEXT_CATEGORIES } from '@shared/types/tenant-context';

const categoryLabel = (key: string) => CONTEXT_CATEGORIES.find((category) => category.key === key)?.label ?? key;

/**
 * Prepares the change a recommendation asks for, shows it to edit, then applies
 * it. Nothing is written before « Appliquer ».
 */
export const ApplyPanel = ({ proposalId, onApplied, onCancel }: { proposalId: string; onApplied: () => void; onCancel: () => void }) => {
  const [draft, setDraft] = useState<ApplyDraft | null>(null);
  const [before, setBefore] = useState<Map<string, { title: string; content: string }>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const prepared = await prepareApply(proposalId);
        const current = prepared.area === 'profile'
          ? await loadProfileEntries(prepared.changes.flatMap((change) => change.entryId ?? []))
          : new Map();
        if (alive) { setDraft(prepared); setBefore(current); }
      } catch (cause) {
        if (alive) setError(cause instanceof Error ? cause.message : 'La préparation n’a pas abouti.');
      }
    })();
    return () => { alive = false; };
  }, [proposalId]);

  const apply = async () => {
    if (!draft) return;
    setApplying(true);
    setError(null);
    try {
      if (draft.area === 'profile') await applyProfileChanges(draft.changes);
      else await createCompanyObjective(draft);
      onApplied();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'L’application a échoué.');
    } finally {
      setApplying(false);
    }
  };

  if (!draft) {
    return (
      <div className="mt-3 rounded-lg border border-domino-cardBorder bg-white/60 px-4 py-3 text-sm">
        {error
          ? <div className="flex items-center justify-between gap-3"><span className="text-red-700">{error}</span><Button variant="ghost" size="sm" onClick={onCancel}>Fermer</Button></div>
          : <span className="text-domino-inkSoft">L’analyste prépare la modification…</span>}
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-4 rounded-lg border border-domino-cardBorder bg-white/60 px-4 py-4">
      {draft.area === 'profile' ? draft.changes.map((change, index) => {
        const old = change.entryId ? before.get(change.entryId) : undefined;
        const update = (patch: Partial<typeof change>) => setDraft({ ...draft, changes: draft.changes.map((entry, at) => (at === index ? { ...entry, ...patch } : entry)) });
        return (
          <div key={`${change.entryId ?? 'new'}-${index}`} className="space-y-2">
            <p className="text-xs text-domino-inkSoft">
              Profil · {categoryLabel(change.category)} · {old ? 'entrée modifiée' : 'nouvelle entrée'}{change.why ? ` — ${change.why}` : ''}
            </p>
            <Input value={change.title} onChange={(event) => update({ title: event.target.value })} aria-label="Titre de l’entrée" />
            <Textarea value={change.content} onChange={(event) => update({ content: event.target.value })} rows={6} aria-label="Contenu de l’entrée" className="text-sm" />
            {old && (
              <details className="text-xs text-domino-inkSoft">
                <summary className="cursor-pointer">Texte actuel</summary>
                <p className="mt-1 whitespace-pre-wrap">{old.content}</p>
              </details>
            )}
          </div>
        );
      }) : (
        <div className="space-y-2">
          <p className="text-xs text-domino-inkSoft">Objectif de l’entreprise{draft.why ? ` — ${draft.why}` : ''}</p>
          <Input value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} aria-label="Titre de l’objectif" maxLength={180} />
          <Textarea value={draft.successCriteria} onChange={(event) => setDraft({ ...draft, successCriteria: event.target.value })} rows={4} aria-label="Critère de réussite" className="text-sm" />
          <label className="flex items-center gap-2 text-sm text-domino-inkSoft">
            Échéance
            <Input type="date" value={draft.dueDate ?? ''} onChange={(event) => setDraft({ ...draft, dueDate: event.target.value || null })} className="w-44" />
          </label>
        </div>
      )}
      {error && <p className="text-sm text-red-700">{error}</p>}
      <div className="flex gap-2">
        <Button size="sm" onClick={() => void apply()} loading={applying}>{draft.area === 'profile' ? 'Appliquer au Profil' : 'Créer l’objectif'}</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={applying}>Annuler</Button>
      </div>
    </div>
  );
};
